import { useMemo, useState } from "react";
import { normalizeText } from "@/lib/text";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import TemplatePreviewDialog from "@/components/TemplatePreviewDialog";
import {
  MagnifyingGlass,
  Sparkle,
  ArrowRight,
  FileText,
  Pill,
  Lightning,
  HandHeart,
  Bone,
  Brain,
  Users,
  Moon,
  Drop,
  Heartbeat,
  Wind,
  Syringe,
  Palette,
  GenderFemale,
  Baby,
  Eye,
  Ear,
  ForkKnife,
  CheckCircle,
} from "@phosphor-icons/react";

const CATEGORY_ICONS = {
  "Addictologie": Pill,
  "Cardiologie": Heartbeat,
  "Dermatologie": Palette,
  "Diabétologie": Syringe,
  "Douleur": Lightning,
  "Gastro-entérologie": ForkKnife,
  "Gériatrie": HandHeart,
  "Gynécologie": GenderFemale,
  "Neurologie": Brain,
  "Ophtalmologie": Eye,
  "ORL": Ear,
  "Pédiatrie": Baby,
  "Pneumologie": Wind,
  "Rhumatologie": Bone,
  "Santé mentale": Brain,
  "Santé publique": Users,
  "Sommeil": Moon,
  "Urologie": Drop,
};
const catIcon = (c) => CATEGORY_ICONS[c] || FileText;

/**
 * Modal to browse validated templates.
 * - Category filter pills + count
 * - Compact dense rows grouped by specialty
 * - Instant search (title, category, description)
 * - Click a row (or the Utiliser button) to pick
 */
export default function TemplateLibraryDialog({ open, onOpenChange, templates, onPick, mineSlugs }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [previewTpl, setPreviewTpl] = useState(null);

  const filtered = useMemo(() => {
    const s = normalizeText(q.trim());
    if (!s) return templates;
    return templates.filter((t) =>
      normalizeText(`${t.title} ${t.category || ""} ${t.description || ""}`).includes(s)
    );
  }, [q, templates]);

  const counts = useMemo(() => {
    const m = {};
    for (const t of templates) {
      const c = t.category || "Autre";
      m[c] = (m[c] || 0) + 1;
    }
    return m;
  }, [templates]);

  const sortedCats = useMemo(
    () => Object.keys(counts).sort((a, b) => a.localeCompare(b, "fr")),
    [counts]
  );

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const c = t.category || "Autre";
      if (cat !== "all" && c !== cat) continue;
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(t);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, "fr"));
  }, [filtered, cat]);

  const pick = (t) => {
    onPick(t.template_id);
    onOpenChange(false);
    setQ("");
    setCat("all");
    setPreviewTpl(null);
  };

  const already = (t) => t.slug && mineSlugs?.has?.(t.slug);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E1E9F0]">
          <DialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F] flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-[#3A8FA5]" />
            Bibliothèque validée
          </DialogTitle>
          <p className="text-sm text-[#5F758F] mt-1">
            Choisissez un questionnaire reconnu comme point de départ. Vous pourrez tout modifier ensuite.
          </p>
          <div className="relative mt-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F758F]" />
            <Input
              autoFocus
              placeholder="Rechercher par nom, spécialité, description…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5] rounded-[10px]"
              data-testid="library-search"
            />
          </div>

          {/* Category filter pills */}
          <div className="mt-4 flex flex-wrap gap-2" data-testid="library-cat-filters">
            <button
              type="button"
              onClick={() => setCat("all")}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${cat === "all" ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white text-[#5F758F] border-[#E1E9F0] hover:border-[#3A8FA5] hover:text-[#1E3A5F]"}`}
              data-testid="library-cat-all"
            >
              Toutes
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${cat === "all" ? "bg-white/15 text-white" : "bg-[#F4F7FB] text-[#5F758F]"}`}>{templates.length}</span>
            </button>
            {sortedCats.map((c) => {
              const Icon = catIcon(c);
              const active = cat === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCat(c)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${active ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white text-[#5F758F] border-[#E1E9F0] hover:border-[#3A8FA5] hover:text-[#1E3A5F]"}`}
                  data-testid={`library-cat-${c}`}
                >
                  <Icon size={13} weight="duotone" className={active ? "text-white" : "text-[#3A8FA5]"} />
                  {c}
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${active ? "bg-white/15 text-white" : "bg-[#F4F7FB] text-[#5F758F]"}`}>{counts[c]}</span>
                </button>
              );
            })}
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
          {grouped.length === 0 ? (
            <p className="text-sm text-[#5F758F] text-center py-12">Aucun questionnaire ne correspond.</p>
          ) : grouped.map(([c, list]) => {
            const Icon = catIcon(c);
            return (
              <div key={c}>
                <div className="flex items-center gap-2.5 mb-2.5">
                  <Icon size={16} weight="duotone" className="text-[#3A8FA5]" />
                  <h4 className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold">{c}</h4>
                  <span className="text-[12px] text-[#5F758F]">{list.length}</span>
                  <span className="h-px flex-1 bg-[#E1E9F0]" />
                </div>
                <div className="bg-white border border-[#E1E9F0] rounded-[14px] divide-y divide-[#E1E9F0] overflow-hidden">
                  {list.map((t) => {
                    const isAlready = already(t);
                    return (
                      <div
                        key={t.template_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setPreviewTpl(t)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPreviewTpl(t); } }}
                        className="group w-full text-left grid grid-cols-[auto_1fr_auto] items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[#F4F7FB] cursor-pointer focus:outline-none focus-visible:bg-[#F4F7FB]"
                        data-testid={`library-card-${t.template_id}`}
                      >
                        <span className="w-9 h-9 rounded-[10px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center shrink-0">
                          <Icon size={16} weight="duotone" className="text-[#3A8FA5]" />
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[14.5px] font-semibold text-[#1E3A5F] leading-tight">{t.title}</p>
                            {isAlready && (
                              <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[#3A8FA5] font-semibold whitespace-nowrap bg-[#E6F1F5] border border-[#C7DEE6] rounded-full px-2 py-0.5">
                                <CheckCircle size={11} weight="fill" /> Ajouté
                              </span>
                            )}
                          </div>
                          <p className="text-[12.5px] text-[#5F758F] mt-1 line-clamp-1" title={t.description || ""}>{t.description || "—"}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[12px] text-[#5F758F] tabular-nums hidden sm:inline">{t.questions.length} Q</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); pick(t); }}
                            className="inline-flex items-center gap-1 rounded-[10px] border border-[#E1E9F0] text-[#3A8FA5] hover:bg-[#3A8FA5] hover:text-white hover:border-[#3A8FA5] font-semibold h-8 px-3 text-[13px] transition-colors"
                            data-testid={`library-use-${t.template_id}`}
                          >
                            Utiliser
                            <ArrowRight size={13} className="ml-0.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-3 border-t border-[#E1E9F0] flex justify-between items-center bg-[#F4F7FB]">
          <p className="text-xs text-[#5F758F]">{filtered.length} questionnaire{filtered.length > 1 ? "s" : ""}{cat !== "all" ? ` · ${cat}` : ""}</p>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="library-close">
            Fermer
          </Button>
        </div>
      </DialogContent>
      <TemplatePreviewDialog
        open={!!previewTpl}
        template={previewTpl}
        onClose={() => setPreviewTpl(null)}
        onUse={() => { if (previewTpl) pick(previewTpl); }}
        useLabel={previewTpl?.slug && mineSlugs?.has?.(previewTpl.slug) ? "Utiliser une copie" : "Utiliser ce modèle"}
        headerLabel="Aperçu du modèle"
      />
    </Dialog>
  );
}
